import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import FaqMini from '../components/faq/mini';
import Framework from '../components/framework';
import AlwaysIncluded from '../components/framework/always-included';
import PageHeader from '../components/framework/pages/page-header';
import SectionHeader from '../components/framework/sections/section-header';
import LocationMap from '../components/location/map';
import LocationMapGeoLocation from '../components/location/map_geo_location';
import LocationMapList from '../components/location/map_list';
import LocationMarkers from '../components/location/map_markers';
import LocationMapSearch from '../components/location/map_search';
import Trustpilot from '../components/script/trustpilot';
import Statistics from '../components/statistics/statistics';
import ProductList from '../components/test/product-list';
import Trans from '../components/translation/trans';
import BarTrust from '../components/trust/bar-trust';
import { MapProvider } from '../context/map';
import useSiteMetadata from '../hooks/use_site_metadata';
import useTranslation from '../hooks/use_translation';

const HealthCheckPage = ({ location }) => {
	const { t } = useTranslation('healthcheck');
	const siteMetadata = useSiteMetadata();

	const [hide, setHide] = useState(false);

	const handleGotoMarker = () => {
		setHide(true);
	};

	return (
		<Framework
			description={t('meta_description')}
			location={location}
			tags={t('meta_keywords')}
			title={t('title')}
			tracking={{ content_group: 'HealthCheck' }}
		>
			<section
				className=" section position-relative pb-5 bg-gray-100"
				id="content"
			>
				<div className="container">
					<div className="row align-items-center ">
						<div className="col-12">
							<PageHeader
								excerpt={t('t2')}
								pageVariant="center"
								title={t('t1')}
							/>
						</div>
						<div className="col-12">
							<Trustpilot
								className="my-4 my-lg-5 mx-0"
								theme="light"
								type="starter"
							/>
							<ProductList
								exclude_groups={[...siteMetadata.config.business_tests_groups]}
								grouping={false}
								include_product_types={['checkup']}
								include_products={[127]}
								limit={1}
								minimum_markers={2}
								sort={{ key: 'position', order: 'desc' }}
								tracking={{
									item_list_name: 'Checkup products on health check page',
									source: 'catalog',
								}}
								variant="featured"
							/>
						</div>
					</div>
				</div>
			</section>
			<section className="bg-gray-100 py-5">
				<div className="container  ">
					<BarTrust size="lg" variant="cards" />
				</div>
			</section>
			<section className="py-5">
				<div className="container  ">
					<div className="row justify-content-center">
						<div className="col-lg-8 article-content">
							<Trans i18nKey="t3" t={t} />
						</div>
					</div>
				</div>
			</section>
			<section className="section bg-gray-100 py-lg-5 py-4 rounded-4" id="">
				<Statistics />
			</section>
			<section className="section" id="">
				<AlwaysIncluded />
			</section>
			<section className="" id="healthcheck-locations">
				<MapProvider>
					<div className={`location-map-container position-relative`}>
						<LocationMap />
						<LocationMarkers />

						<div className="location-list-left p-3  ">
							<div className="p-3 bg-white shadow-sm rounded-3 border border-1">
								<h3 className="large fw-700 mb-3">{t('t16')}</h3>
								<LocationMapSearch />
								<LocationMapGeoLocation />
								<div className={`location-list px-1 mt-3 mt-lg-0 bg-white`}>
									<LocationMapList handleGotoMarker={handleGotoMarker} />
								</div>
							</div>
						</div>
					</div>
				</MapProvider>
			</section>
			<section className="section">
				<div className="container">
					<SectionHeader hideButton title={t('t13')} />
					<ProductList
						exclude_groups={[...siteMetadata.config.business_tests_groups]}
						grouping={false}
						include_product_types={['checkup']}
						minimum_markers={2}
						sort={{ key: 'position', order: 'desc' }}
						tracking={{
							item_list_name: 'Checkup products on health check page',
							source: 'catalog',
						}}
						variant="card_grid"
					/>
				</div>
			</section>

			<section className="faq section">
				<div className="container ">
					<div className="row mb-3 justify-content-between">
						<div className="col-lg-5">
							<h3 className="title-medium mb-0 pe-4">{t('t14')}</h3>

							<p className="small text-muted mt-2 article-content">
								<Trans i18nKey="t15" t={t} />
							</p>
						</div>
						<div className="col-lg-6">
							<FaqMini
								include_groups={
									siteMetadata.config.support_product_groups.blood_sample
								}
							/>
						</div>
					</div>
				</div>
			</section>
		</Framework>
	);
};

HealthCheckPage.propTypes = {
	data: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
};

export default HealthCheckPage;

export const query = graphql`
	query {
		allNewLocation {
			nodes {
				location_id
			}
		}

		locales: allLocale {
			...translationFields
		}
	}
`;
